import { createLogger } from "redux-logger";
import createSagaMiddleware, { END } from "redux-saga";

import rootReducer, { type RootState } from "@/rootReducer";
import sagaMonitor from "@redux-saga/simple-saga-monitor";
import { AnyAction, configureStore } from "@reduxjs/toolkit";

import rootSaga from "../rootSaga";
import { makeMiddleware } from "./make-middleware";

const hasReactRefPayload = (action: AnyAction) => !!action.payload?.current;
const hasReactRefMetaArg = (action: AnyAction) => !!action.meta?.arg?.current;
const hasSymbolType = (action: AnyAction) => typeof action.type === "symbol";

const actionSanitizer = action => {
  if (hasReactRefPayload(action)) {
    action.payload = "<REACT_REF_OBJECT>";
  }

  if (hasReactRefMetaArg(action)) {
    action.meta.arg = "<REACT_REF_OBJECT>";
  }

  if (hasSymbolType(action)) {
    action.type = action.type.toString();
  }

  return action;
};

const logger = createLogger({
  collapsed: true,
});

export default function makeStore() {
  const sagaMiddleware = createSagaMiddleware({
    sagaMonitor,
    onError(error: Error) {
      console.error(error);
    },
  });

  const middleware = [sagaMiddleware, logger];

  const store = configureStore({
    reducer: rootReducer,
    middleware: makeMiddleware<RootState>(middleware),
    devTools: {
      actionSanitizer,
    },
  });

  // not using sagas on the server (yet)
  if (typeof window !== "undefined") {
    store.sagaTask = sagaMiddleware.run(rootSaga);
    store.close = () => store.dispatch(END);
  }

  if (module.hot) {
    // Enable Webpack hot module replacement for reducers
    module.hot.accept("../rootReducer", () => {
      const nextRootReducer = require("../rootReducer").default;
      store.replaceReducer(nextRootReducer);
    });

    // Enable Webpack hot module replacement for sagas
    module.hot.accept("../rootSaga", () => {
      const newRootSaga = require("../rootSaga").default;
      store.sagaTask.cancel();
      store.sagaTask.toPromise().then(() => {
        store.sagaTask = sagaMiddleware.run(newRootSaga);
      });
    });
  }

  return store;
}
